import { LookupValue } from "@rightship-tech/common-ui/models/lookup.models";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { autoinject, computedFrom, transient, useView } from "aurelia-framework";
import { I18N } from "aurelia-i18n";
import { BindingSignaler } from "aurelia-templating-resources";
import { Claims } from "../../../../../../constants/authentication/claims";
import { RightShipDialogServiceConstants } from "../../../../../../constants/basics/rightship-dialog.constants";
import { FileHelper } from "../../../../../../helpers/file-helper";
import { EntityAttachmentModel } from "../../../../../../models/attachment/attachment.models";
import { PscClientModel, PscDeficiencyClientModel, PscInspectionDeficiencyStatusEnum, SavePscInspectionDeficiencyRequest } from "../../../../../../models/pscs/psc.models";
import { AttachmentClientService } from "../../../../../../services/attachment/attachments-client.service";
import { UserClaimsService } from "../../../../../../services/authentication/user-claims.service";
import { NotificationClientService, NotificationType } from "../../../../../../services/basics/notification-client.service";
import { PscService } from "../../../../../../services/psc/psc.service";
import { QueriesClientService } from "../../../../../../services/queries/queries-client.services";
import { AttachmentsHelper } from "../../../../../../utilities/attachments-helper";
import { ChildComponent } from "../../../child-component";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";
import JSZip from 'jszip';
import { ValidationControllerFactory, ValidationController, ValidationRules } from "aurelia-validation";
import { CustomValidationRenderer } from "../../../../../../client-validation-system/custom-validation-renderer";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { EventAggregator } from "aurelia-event-aggregator";
import { Messages } from "../../../../../../configuration/event-messages.constants";

@autoinject
@transient()
@useView('views/pages/modules/psc/steps/deficiencies/psc-deficiencies.html')
export class PscDeficiencies extends ChildComponent<PscClientModel> {

    public showLegacyUi: boolean; //New UI flag since feature will be turned off in prod

    //#region Legacy Properties
    private isLoadingAttachments: boolean = false;
    isShowingExtraDetails: boolean = true;
    showMoreText: { [key: string]: boolean } = {};
    canViewPSCDocuments: boolean;
    //#endregion

    //#region New UI Properties
    private _isAllSectionsCollapsed: boolean;
    private _sectionCollapsableToggled: { [key: number]: boolean } = {};
    private _images:  { name: string; url: string }[] = [];
    private _validator: ValidationController;
    private _firstLoad: boolean = true;
    public rightShipUser: boolean = false;
    public isPendingReviewFilterActive: boolean;
    public isReviewedFilterActive: boolean;
    public expandAllDefs: boolean;
    public riskSeverityOptions = [
        { value: 1, text: 'High', description: 'Deficiencies that pose a critical and immediate threat to the safety of the vessel, crew, environment or operations. They require immediate action and urgent correction. Failure to address these issues could lead to severe incidents, detentions or legal consequences. ' },
        { value: 2, text: 'Medium', description: 'Deficiencies that present a potential impact/threat to the safety of the vessel, crew, cargo, environment or operations. These items pose a moderate risk and, if left unaddressed, could escalate into more serious problems that may cause significant disruption. Corrective action is necessary to prevent further escalation.  ' },
        { value: 3, text: 'Low', description: 'Deficiencies that have minimal or no immediate impact/ threat to the safety of the vessel, crew, cargo, environment or operations. These issues pose a minor risk and do not interrupt operations significantly.' },
        { value: 4, text: 'Not active in Safety Score', description: '' }
    ]
    public defCategories: { id: number, value: string, displayName: string }[];
    public selectedDefCategories: { id: number, value: string, displayName: string }[] = [];
    public filterCount: number; //Used to trigger UI updates
    public partialExtraction: boolean;
    public exhaustiveExtraction: boolean;
    public extractionInProgess: boolean;
    public pscIsClosed: boolean;
    public showDefExtraInfo: boolean;
    //#endregion

    constructor(notificationsService: NotificationClientService, i18N: I18N, private bindingSignaler: BindingSignaler,
        private queriesClientService: QueriesClientService, private attachmentClientService: AttachmentClientService,
        private userClaimService: UserClaimsService, private dialogClientService: DialogClientService,
        private pscService: PscService, private configService: ConfigService,private validationControllerFactory: ValidationControllerFactory,
        private dialogClient: DialogClientService, private eventAggregator: EventAggregator) {
        super(notificationsService, i18N);
        
        this.rightShipUser = this.userClaimService.hasPermission(Claims.RightShipUser);
        this.canViewPSCDocuments = this.userClaimService.hasPermission(Claims.CanViewPSCDocuments);
        this._validator = this.validationControllerFactory.createForCurrentScope();
        this._validator.addRenderer(new CustomValidationRenderer());
        this.dialogClient = dialogClient;
    }

    async onActivated() {
        this.loadDocuments();
        this.showLegacyUi = !this.item?.enabledNewUI;
        this.pscIsClosed = this.item?.closed;
        this.showDefExtraInfo = this.item?.showDeficiencyExtraInfo;
        if (!this.showLegacyUi && this.rightShipUser) {
            this.loadAiExtractionWarningBanner();

            this.item.deficiencies.forEach(deficiency => {
                deficiency.status = this.getPscDeficiencyStatusText(deficiency);
                deficiency.statusClassName = this.getPscDeficiencyStatusText(deficiency, true);
                deficiency.severity = deficiency.severity ? deficiency.severity : 'No Score';
                deficiency.originalAdminSeverity = deficiency.severity;
                deficiency.beforeUpdateSeverityId = deficiency.severityId;
                deficiency.originalSeverity = deficiency.originalSeverity ? deficiency.originalSeverity : 'No Score';
            });
            this._firstLoad = false;

            const response = await this.pscService.getPscThumbnailZipFile(this.item.pscInspectionId);
            if (!response) {
                return;
            }

            // Unzip using JSZip
            const zip = new JSZip();
            await zip.loadAsync(response);

            // Extract images and create URLs for them
            const imagePromises = [];
            zip.forEach((relativePath, zipEntry) => {
                if (!zipEntry.dir && /\.(jpg|jpeg|png|gif)$/.test(relativePath)) { // Filter for image types
                    imagePromises.push(zipEntry.async('blob').then((blob) => {
                        return {
                            name: relativePath, // Get the file name
                            url: URL.createObjectURL(blob) // Create a URL for the image blob,
                        };
                    }));
                }
            });

            this._images = await Promise.all(imagePromises); // Set the images array
            this.item.deficiencies.forEach(deficiency => {
                deficiency.aiData?.thumbnails?.forEach(thumbnail => {
                    const image = this._images.find(x => thumbnail.thumbnailFileName.includes(x.name) || x.name.includes(thumbnail.thumbnailFileName));
                    thumbnail.src = image?.url;
                });
            });
        }
    }

    attached() {
        this.getUniqueCategories();
    }

    //#region Legacy Methods

    getLongText(fullText: string, key: string) {
        if (this.showMoreText && Object.keys(this.showMoreText).indexOf(key) > -1) {
            if (this.showMoreText[key])
                return fullText;
        }

        return fullText ? fullText.substring(0, 100) + (fullText.length > 100 ? '...' : '') : '';
    }

    toggleReadMore(key: string) {
        if (!this.showMoreText)
            this.showMoreText = {};

        this.showMoreText[key] = Object.keys(this.showMoreText).indexOf(key) > -1 ? !this.showMoreText[key] : true;

        this.bindingSignaler.signal('read-more-toggled');
    }


    loadDocuments() {
        if (this.item) {
            return new Promise<void>((resolve, reject) => {
                if (this.isLoadingAttachments || (Object.keys(this.item).indexOf("pscDocuments") > -1 && this.item["pscDocuments"])) {
                    resolve();
                    return;
                }

                this.isLoadingAttachments = true;
                this.queriesClientService.getData('odata/RightShipAttachments/Genesis.GetAttachments(entityTypeId=' + this.entityType + ',entityId=' + this.item.pscInspectionId + ')')
                    .then(results => {
                        this.item["pscDocuments"] = results.value;
                        this.isLoadingAttachments = false;
                        resolve();
                    }).catch(() => {
                        this.isLoadingAttachments = false;
                        reject();
                    });
            });
        }
    }

    public openDocument(item: EntityAttachmentModel, fileName?: string) {
        this.attachmentClientService.getSharedAccessSignatureToken(item.entityAttachmentId, true).then(sasToken => {
            let url = AttachmentsHelper.getUrl(item.path);
            if (sasToken) {
                url += ((sasToken.startsWith("?") ? "" : "?") + sasToken);
            }
            fileName = fileName || item.name || '';
            FileHelper.downloadFile(url, fileName);
        }).catch(() => {
            this.notificationService.show("Error", "You do not have enough permissions to open/download this file", NotificationType.Error);
        });
    }

    //#endregion

    //#region New UI Methods

    @computedFrom('filterCount')
    get pendingReviewCount() {
        return this.item.deficiencies.filter(x => x.pscInspectionDeficiencyStatusId == PscInspectionDeficiencyStatusEnum.PendingReview || x.pscInspectionDeficiencyStatusId == null).length;
    }

    @computedFrom('filterCount')
    get reviewedCount() {
        return this.item.deficiencies.filter(x => x.pscInspectionDeficiencyStatusId == PscInspectionDeficiencyStatusEnum.ScoredAndReviewed).length;
    }

    @computedFrom('item.deficiencies', 'selectedDefCategories', 'isPendingReviewFilterActive', 'isReviewedFilterActive')
    get filteredDeficiencies(): PscDeficiencyClientModel[] {
        let filteredDeficiencies: PscDeficiencyClientModel[] = this.item?.deficiencies || [];

        //Filter by selected categories
        if (this.selectedDefCategories && this.selectedDefCategories.length > 0) {
            filteredDeficiencies = filteredDeficiencies.filter(x =>
                this.selectedDefCategories.some(y => y.value === x.group)
            );
        }

        //Filter by statuses if active
        filteredDeficiencies = filteredDeficiencies.filter(x => {
            const isPendingReview = this.isPendingReviewFilterActive && (x.pscInspectionDeficiencyStatusId === PscInspectionDeficiencyStatusEnum.PendingReview || x.pscInspectionDeficiencyStatusId == null);
            const isReviewed = this.isReviewedFilterActive && x.pscInspectionDeficiencyStatusId === PscInspectionDeficiencyStatusEnum.ScoredAndReviewed;

            //Show all if neither filter is active
            return (!this.isPendingReviewFilterActive && !this.isReviewedFilterActive) || isPendingReview || isReviewed;
        });

        return filteredDeficiencies;
    }

    public pendingReviewFilterClick() {
        this.isPendingReviewFilterActive = !this.isPendingReviewFilterActive;
    }

    public reviewedFilterClick() {
        this.isReviewedFilterActive = !this.isReviewedFilterActive;
    }

    // Clear all filters
    public clearAllFilters() {
        this.isPendingReviewFilterActive = false;
        this.isReviewedFilterActive = false;

        this.selectedDefCategories = [];
    }

    //Add Deficiency
    public addDeficiency() {
        this.clearAllFilters();

        const newDef : PscDeficiencyClientModel = {
            pscDeficiencyId: null,
            deficiency: '',
            code: '',
            group: '',
            actionId: null,
            severity: 'No Score',
            severityId: null,
            action: '',
            actionCode: '',
            immediateCause: '',
            rootCause: '',
            correctiveActions: '',
            preventativeActions: '',
            pscInspectionDeficiencyStatus: 'Pending Review',
            pscInspectionDeficiencyStatusId: 1,
            riskArea: '',
            pscInspectionDeficiencyId: null,
            deficiencyDetails: '',
            aiData: null,
            hasCustomerCloseOutUpdatedUiContent: false,
            hasSeverityScoringUpdatedUiContent: false,
            status: 'Pending Review',
            statusClassName: 'pending-review',
            thumbnails: null,
            aiInspectionDeficiencyId: null,
            isNew: true,
            knownDeficiencyCode: false,
            isManuallyAdded: true
        }

        if (this.item?.deficiencies) {
            this.item.deficiencies.push(newDef)
        } else {
            this.item.deficiencies = [newDef];
        }
        this.bindingSignaler.signal('change-deficiency');

        const defIndex = this.filteredDeficiencies.length - 1;

        //If section is collapsed, expand it
        this.isCollapsed(defIndex) ? this.toggleSectionsCollapsed(defIndex) : null;
        
        //Scroll to new deficiency
        this.scrollToDeficiency(defIndex);
    }

    public openAiInsightsDialog(deficiency: PscDeficiencyClientModel) {

        if ((!this.hasCustomerCloseOutAiDataReset(deficiency) && this.hasCustomerCloseOutAiData(deficiency)) || (!this.hasSeverityScoringAiDataReset(deficiency) && this.hasSeverityScoringAiData(deficiency))) {
            return;
        }

        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.DeficiencyAiInsightsDialog,
            null, true, false, false, "right");
    }

    hasCustomerCloseOutAiDataReset(deficiency: PscDeficiencyClientModel) {
        return this.isEqual(deficiency.immediateCause, deficiency.aiData?.immediateCause) &&
            this.isEqual(deficiency.rootCause, deficiency.aiData?.rootCause) &&
            this.isEqual(deficiency.correctiveActions, deficiency.aiData?.correctiveActions) &&
            this.isEqual(deficiency.preventativeActions, deficiency.aiData?.preventativeActions) &&
            this.isEqual(deficiency.deficiencyDetails, deficiency.aiData?.deficiencyDetails) &&
            deficiency.actionId == deficiency.aiData?.actionId;
    }

    isEqual(a: string, b: string) {
        return a == b || (a == null && b == "") || (a == "" && b == null);
    }

    hasSeverityScoringAiDataReset(deficiency: PscDeficiencyClientModel) {
        return deficiency.severityId == deficiency.aiData?.severityId;
    }

    toggleAllSectionsCollapsed() {
        this._isAllSectionsCollapsed = !this.expandAllDefs;

        //Clear array
        this._sectionCollapsableToggled = {};
        this.bindingSignaler.signal('update-all-sections-collapsed');
    }

    toggleSectionsCollapsed(index: number) {
        if (!this._sectionCollapsableToggled) this._sectionCollapsableToggled = {};

        if (!this._isAllSectionsCollapsed && Object.keys(this._sectionCollapsableToggled).indexOf(index.toString()) == -1)
            this._sectionCollapsableToggled[index] = true;

        this._sectionCollapsableToggled[index] =
            //true when is open. set to true if def index is false or is not in _sectionCollapsableToggled
            !(this._sectionCollapsableToggled && Object.keys(this._sectionCollapsableToggled).indexOf(index.toString()) > -1 && this._sectionCollapsableToggled[index]);

        this.expandAllDefs = Object.keys(this._sectionCollapsableToggled).filter(k => this._sectionCollapsableToggled[k] == true).length == 0;
    }

    isCollapsed(index: number) {
        if (index == null) return false;

        const isKeyInTogglesObj = this._sectionCollapsableToggled && Object.keys(this._sectionCollapsableToggled).indexOf(index.toString()) > -1;
        const isSectionToggled = isKeyInTogglesObj && (this._sectionCollapsableToggled[index] ?? false);
        const collapsableOpen = (!this._isAllSectionsCollapsed && (!isKeyInTogglesObj || isSectionToggled)) || (this._isAllSectionsCollapsed && isSectionToggled);
        return !collapsableOpen;
    }

    deficiencyCodesFilter(originalItems: LookupValue[]): LookupValue[] {
        const items = originalItems.filter(x => !(x.additionalData && x.additionalData.code == '-1'));
        return items;
    }

    private getUniqueCategories() {
        const categoryMap = new Map<string, number>();
        let id = 1;

        //Loop through deficiencies and count occurrences of each riskArea
        this.item.deficiencies
            .filter(deficiency => deficiency.group !== null)
            .forEach(deficiency => {
                const category = deficiency.group;

                //Increment the count for the current risk area in the map
                categoryMap.set(category, (categoryMap.get(category) || 0) + 1);
            });

        //Create an array of risk area objects
        this.defCategories = Array.from(categoryMap, ([value, count]) => ({
            id: id++,
            value: value,
            displayName: `${value} (${count})`,
        }));
    }

    restoreCustomerCloseOutAiContent(deficiency: PscDeficiencyClientModel) {
        deficiency.immediateCause = deficiency.aiData?.immediateCause;
        deficiency.rootCause = deficiency.aiData?.rootCause;
        deficiency.correctiveActions = deficiency.aiData?.correctiveActions;
        deficiency.preventativeActions = deficiency.aiData?.preventativeActions;
        deficiency.actionId = deficiency.aiData?.actionId;
        deficiency.deficiencyDetails = deficiency.aiData?.deficiencyDetails;
        deficiency.pscDeficiencyId = deficiency.aiData?.pscDeficiencyId;
    }

    restoreSeverityScoringAiContent(deficiency: PscDeficiencyClientModel) {
        deficiency.severityId = deficiency.aiData?.severityId;
    }

    getPscDeficiencyStatusText(deficiency: PscDeficiencyClientModel, isClassName?: boolean): string {

        if (isClassName) {
            switch (deficiency.pscInspectionDeficiencyStatusId) {
                case PscInspectionDeficiencyStatusEnum.PendingReview:
                    return "pending-review";
                case PscInspectionDeficiencyStatusEnum.ScoredAndReviewed:
                    return "scored-and-reviewed";
                default:
                    return "pending-review";
            }
        } else {
            switch (deficiency.pscInspectionDeficiencyStatusId) {
                case PscInspectionDeficiencyStatusEnum.PendingReview:
                    return "Pending Review";
                case PscInspectionDeficiencyStatusEnum.ScoredAndReviewed:
                    return "Scored and Reviewed";
                default:
                    return "Pending Review";
            }
        }
    }

    async scoreAndReviewDeficiency(deficiency: PscDeficiencyClientModel, index: number) {        
        // validate new deficiency
        ValidationRules
            .ensure<PscDeficiencyClientModel, number>(x => x.pscDeficiencyId)
            .required()
            .withMessage("Invalid PSC Deficiency")
            .ensure(x => x.severityRemarks)
            .required()
            .withMessage("Severity Remarks is required")
            .on(deficiency);

        this._validator.validate().then(v => {
            if(v.valid)
            {
                this.filterCount++;
                deficiency.pscInspectionDeficiencyStatusId = PscInspectionDeficiencyStatusEnum.ScoredAndReviewed;
                deficiency.status = this.getPscDeficiencyStatusText(deficiency);
                deficiency.statusClassName = this.getPscDeficiencyStatusText(deficiency, true);
                this.savePscInspectionDeficiency(deficiency, index);
            }
        });
    }

    deleteDeficiency(deficiency: PscDeficiencyClientModel, index: number) {
        if (!deficiency.isManuallyAdded) {
            this.notificationService.show("Only manually added deficiency can be deleted", "", NotificationType.Error);
            return;
        }

        this.dialogClient.show(RightShipDialogServiceConstants.URLs.ConfirmDeleteDialog, {
            entity: "Deficiency",
            canHide: false,
            delete: async () => {
                if (deficiency.isNew) {
                    this.removeDeficiencyItem(index);
                } else {
                    this.pscService.deletePscInspectionDeficiency(deficiency.pscInspectionDeficiencyId).then(() => {
                        this.notificationService.show("Deficiency has been deleted", "", NotificationType.Success);
                        this.removeDeficiencyItem(index);
                    }).catch(() => {
                        this.notificationService.show("Error deleting deficiency", "", NotificationType.Error);
                    });
                }

                this.dialogClient.hide();
            }
        }, false, false, false);
    }

    removeDeficiencyItem(index: number) : boolean {
        if (index > -1) {
            this.item.deficiencies.splice(index, 1);
            this.eventAggregator.publish(Messages.changePscDeficiency, this.item.deficiencies);
            this.bindingSignaler.signal('change-deficiency');
            this.filterCount--;
            this.loadAiExtractionWarningBanner();
            return true;
        }
        return false;
    }

    handlePscDeficiencyCodeChanged(deficiency: PscDeficiencyClientModel, code: string, name: string) {
        if (code) {
            deficiency.code = code;
        }

        if (name) {
            deficiency.deficiency = name;
        }

        this.handleCustomerCloseOutDataChanged(deficiency);
    }

    handleCustomerCloseOutDataChanged(deficiency: PscDeficiencyClientModel) {
        deficiency.hasCustomerCloseOutUpdatedUiContent = this.hasCustomerCloseOutAiData(deficiency) && 
            !this.hasCustomerCloseOutAiDataReset(deficiency);

        if (!this._firstLoad) {
            deficiency.hasUpdatedUiContent = true;
        }
    }

    hasCustomerCloseOutAiData(deficiency: PscDeficiencyClientModel) {
        return deficiency.aiData != null && (deficiency.aiData.immediateCause != null ||
            deficiency.aiData.rootCause != null || 
            deficiency.aiData.correctiveActions != null || 
            deficiency.aiData.preventativeActions != null ||
            deficiency.aiData.actionId != null ||
            deficiency.aiData.deficiencyDetails != null);
    }

    selectRiskSeverity(deficiency: PscDeficiencyClientModel, severity) {
        deficiency.hasSeverityScoringUpdatedUiContent = this.hasSeverityScoringAiData(deficiency) && !this.hasSeverityScoringAiDataReset(deficiency);
        deficiency.severityId = severity.value;
        deficiency.severity = severity.text;
        deficiency.severityRemarks = null;
    }

    hasSeverityScoringAiData(deficiency: PscDeficiencyClientModel) {
        return deficiency.aiData != null && deficiency.aiData.severityId != null;
    }

    savePscInspectionDeficiency(deficiency: PscDeficiencyClientModel, index: number) {
        const savePscInspectionDeficiencyRequest = {
            pscInspectionId: this.item.pscInspectionId,
            pscInspectionDeficiencyId: deficiency.pscInspectionDeficiencyId,
            pscInspectionDeficiencyStatusId: deficiency.pscInspectionDeficiencyStatusId,
            severityScoringId: deficiency.severityId,
            pscDeficiencyId: deficiency.pscDeficiencyId,
            pscDeficiencyCategoryId : deficiency.pscDeficiencyCategoryId,
            deficiencyDetails: deficiency.deficiencyDetails,
            pscDeficiencyActionId: deficiency.actionId,
            immediateCause: deficiency.immediateCause,
            rootCause: deficiency.rootCause,
            correctiveActions: deficiency.correctiveActions,
            preventativeActions: deficiency.preventativeActions,
            aiInspectionDeficiencyId: deficiency.aiData?.aiInspectionDeficiencyId,
            actualDeficiencyDetails: deficiency.actualDeficiencyDetails,
            severityRemarks: deficiency.severityRemarks
        } as SavePscInspectionDeficiencyRequest;

        this.pscService.savePscInspectionDeficiency(savePscInspectionDeficiencyRequest).then((result: any) => {
            this.notificationService.show("Deficiency has been scored and reviewed", "", NotificationType.Success);
            deficiency.pscInspectionDeficiencyId = result.entityResult.pscInspectionDeficiencyId;
            deficiency.riskArea = result.entityResult.riskArea;
            deficiency.code = result.entityResult.code;
            deficiency.deficiency = result.entityResult.deficiency;
            deficiency.aiData = result.entityResult.aiExtractionCount > 0 ? result.entityResult.aiData : null;
            deficiency.hasUpdatedUiContent = false;
            deficiency.originalAdminSeverity = deficiency.severity;

            this.item.deficiencies[index] = deficiency;
            this.eventAggregator.publish(Messages.changePscDeficiency, this.item.deficiencies); // publish event before update isNew flag

            deficiency.isNew = false;
            
            this.bindingSignaler.signal('change-deficiency'); // publish binding event after updating everything
            this.loadAiExtractionWarningBanner();
        }).catch(() => {
            this.notificationService.show("Error scoring and reviewing deficiency", "", NotificationType.Error);
        });
    }

    openAttachmentViewer(thumbnailFileName?: string) {
        let attachemntViewerUrl = this.configService.config.urls.rightShipPlatformUrl + `/attachment-viewer?entityTypeId=${EntityTypes.PSC}&id=${this.item.pscInspectionId}`;
        
        if (thumbnailFileName) {
            attachemntViewerUrl += "?thumbnailFileName='" + thumbnailFileName + "'";
        } 

        //open attachment viewer in a new tab
        window.open(attachemntViewerUrl, "_blank");
    }

    loadAiExtractionWarningBanner() {
        if (this.item.documentsCount && this.item.aiExtractionCount != 0) {
            if (this.item.aiExtractionCount == -1) {
                this.partialExtraction = false;
                this.exhaustiveExtraction = false;
                this.extractionInProgess = true;
            }
            else if (this.item.aiExtractionCount < this.item.deficiencies.length) {
                this.partialExtraction = true;
                this.exhaustiveExtraction = false;
                this.extractionInProgess = false;
            }
            else if (this.item.aiExtractionCount > this.item.deficiencies.length) {
                this.partialExtraction = false;
                this.exhaustiveExtraction = true;
                this.extractionInProgess = false;
            }
            else if (this.item.aiExtractionCount == this.item.deficiencies.length) {
                this.item.deficiencies.forEach(def => {
                    if (!def.aiData || !def.aiData.immediateCause || !def.aiData.rootCause || !def.aiData.correctiveActions || !def.aiData.preventativeActions ||
                        !def.aiData.actionId || !def.aiData.deficiency || !def.aiData.code || !def.aiData.deficiencyDetails
                    ) {
                        this.partialExtraction = true;
                        this.exhaustiveExtraction = false;
                        this.extractionInProgess = false;
                        return;
                    }
                });
            }
        } else {
            this.partialExtraction = false;
            this.exhaustiveExtraction = false;
            this.extractionInProgess = false;
        }
    }

    scrollToDeficiency(index) {
        
        //Last deficiency is always the new one
        const deficiency = this.filteredDeficiencies[index];

        setTimeout(() => {
            if (deficiency && deficiency.element) {
                deficiency.element.scrollIntoView({ behavior: 'smooth' });
            }
        }, 100);
    }

    getDeficiencySeverityDescription(severityId: number) {
        if (severityId) {
            return this.riskSeverityOptions[severityId-1].description;
        }
    }
    //#endregion    
}
